import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";

import store from "./store";

import ApolloClient from "apollo-client";
import { ApolloProvider } from "@apollo/react-hooks";
import { InMemoryCache } from "apollo-cache-inmemory";
import { createUploadLink } from "apollo-upload-client";
import { ApolloLink, Observable } from "apollo-link";
import { onError } from "apollo-link-error";
import oktaAuth from "./lib/oktaInstance";

const stripTypename = (value) => {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const { __typename, ...rest } = value;
    return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypename(v)]));
  }
  return value;
};

const errorLink = onError(({ networkError }) => {
  if (networkError && networkError.statusCode === 401) {
    oktaAuth.signInWithRedirect({ originalUri: window.location.href });
    return new Observable(() => {});
  }
});

const authLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = stripTypename(operation.variables);
  }

  const token = oktaAuth.getAccessToken();
  if (token) {
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, Authorization: `Bearer ${token}` },
    }));
  }

  return forward(operation);
});

const client = new ApolloClient({
  defaultOptions: {
    watchQuery: { fetchPolicy: 'no-cache' },
    query: { fetchPolicy: 'no-cache' },
  },
  link: ApolloLink.from([
    errorLink,
    authLink,
    createUploadLink({
      uri: `${process.env.REACT_APP_API_URL}/graphql`,
      credentials: "include",
    }),
  ]),
  cache: new InMemoryCache({
    dataIdFromObject: (obj) => {
      const idFields = {
        Quotes: 'QuoteId',
        QuoteItems: 'LineId',
        QuoteRates: 'QuoteRateId',
        QuoteCAFTemplates: 'QuoteCAFTemplateId',
        QuoteCAFAnswers: 'QuoteCAFAnswerId',
        QuoteVersions: 'VersionId',
        QuoteAccessLog: 'AccessLogId',
        ProposalInserts: 'InsertId',
        Product: 'ProductId',
        ProductRates: 'RateId',
        ProposalTemplates: 'TemplateId',
        ProductPrices: 'ProductPriceId',
        ProductCategory: 'CatId',
        Bundles: 'BundleId',
        BundleItems: 'BundleItemId',
        QuoteTypes: 'QuoteTypeId',
        Settings: 'SettingId',
        Agents: 'AgentId',
        Teams: 'TeamId',
        CAFSection: 'CAFSectionId',
        CAFTemplates: 'CAFTemplateId',
        Customer: 'ContactID',
        User: 'id',
      };
      const field = idFields[obj.__typename];
      if (!field) return false;
      return obj[field] != null ? `${obj.__typename}:${obj[field]}` : false;
    },
  }),
});

const app = (
  <ApolloProvider client={client}>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </ApolloProvider>
);

ReactDOM.render(app, document.getElementById("root"));
serviceWorker.unregister();

document.addEventListener('wheel', (e) => {
  const input = document.activeElement;
  if (input.type === 'number' && input.matches(':hover')) e.preventDefault();
}, { passive: false });
